import { useCallback, useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowDownLeft, FileText, Plus, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ServerDataTable,
  TableSkeleton,
} from "@/components/ui/server-data-table";
import { useServerTable } from "@/components/ui/use-server-table";
import { useNoteColumns } from "@/components/sales-notes/note-columns";
import { NoteListFilters } from "@/components/sales-notes/NoteListFilters";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import {
  useDownloadSalesNotePdf,
  useSalesNotes,
} from "@/lib/api/sales-notes.queries";
import { useActiveCompany } from "@/lib/company-context";
import { translateNoteError } from "@/components/sales-notes/error-translations";
import { downloadBlob } from "@/lib/download-blob";
import type { SalesNoteStatusFilter, SalesNoteSummary } from "@/lib/api/sales-notes";

export const Route = createFileRoute(
  "/companies/$companyId/notas-credito/",
)({
  component: NotasCreditoIndexPage,
});

function NotasCreditoIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <NotasCreditoIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function NotasCreditoIndexContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState<SalesNoteStatusFilter>("all");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");

  const downloadPdf = useDownloadSalesNotePdf("credit", companyId);
  const downloadMutate = downloadPdf.mutateAsync;
  const onDownloadPdf = useCallback(
    (note: SalesNoteSummary) => {
      void (async () => {
        try {
          const blob = await downloadMutate({ noteId: note.id });
          downloadBlob(blob, `${note.documentId}.pdf`);
        } catch (err) {
          toast.error(translateNoteError(err, t));
        }
      })();
    },
    [downloadMutate, t],
  );
  const columns = useNoteColumns({
    companyId,
    onDownloadPdf,
    downloadingId: downloadPdf.isPending
      ? (downloadPdf.variables?.noteId ?? null)
      : null,
  });
  const table = useServerTable({
    defaultSort: { id: "emitDate", desc: true },
    defaultPageSize: 50,
    filterKey: JSON.stringify({ q, status, periodFrom, periodTo }),
  });

  const notesQuery = useSalesNotes("credit", companyId, {
    ...(q ? { q } : {}),
    status,
    ...(periodFrom ? { periodFrom } : {}),
    ...(periodTo ? { periodTo } : {}),
    ...table.params,
  });

  const hasFilters =
    q.length > 0 || status !== "all" || !!periodFrom || !!periodTo;
  const pagination = notesQuery.data?.pagination;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="nota_credito" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {t("notes.list.back")}
          </Link>
          <h1 className="mt-1 inline-flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <ArrowDownLeft className="h-6 w-6 text-primary" />
            {t("notes.title.credit")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("notes.list.subtitle.credit")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => void notesQuery.refetch()}
            disabled={notesQuery.isFetching}
          >
            <RefreshCw
              className={
                notesQuery.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"
              }
            />
            {t("notes.list.refresh")}
          </Button>
          <Button asChild>
            <Link
              to="/companies/$companyId/notas-credito/new"
              params={{ companyId }}
            >
              <Plus className="h-4 w-4" />
              {t("notes.list.new.credit")}
            </Link>
          </Button>
        </div>
      </div>

      <NoteListFilters
        q={q}
        onQChange={setQ}
        status={status}
        onStatusChange={setStatus}
        periodFrom={periodFrom}
        onPeriodFromChange={setPeriodFrom}
        periodTo={periodTo}
        onPeriodToChange={setPeriodTo}
      />

      {notesQuery.isPending ? (
        <TableSkeleton columns={columns} />
      ) : notesQuery.isError ? (
        <ErrorState
          title={t("notes.list.error_title")}
          description={t("notes.list.error_body")}
          onRetry={() => void notesQuery.refetch()}
          retryLabel={t("common.retry")}
          error={notesQuery.error}
        />
      ) : notesQuery.data.notes.length === 0 ? (
        <EmptyState companyId={companyId} hasFilters={hasFilters} />
      ) : (
        <ServerDataTable
          columns={columns}
          data={notesQuery.data.notes}
          pageCount={pagination?.totalPages ?? 1}
          rowCount={pagination?.total ?? 0}
          sorting={table.sorting}
          onSortingChange={table.onSortingChange}
          pagination={table.pagination}
          onPaginationChange={table.onPaginationChange}
          getRowId={(note) => note.id}
          onRowClick={(note) =>
            void navigate({
              to: "/companies/$companyId/notas-credito/$noteId",
              params: { companyId, noteId: note.id },
            })
          }
          isFetching={notesQuery.isFetching}
        />
      )}
    </div>
  );
}

function EmptyState({
  companyId,
  hasFilters,
}: {
  companyId: string;
  hasFilters: boolean;
}) {
  const { t } = useTranslation();
  if (hasFilters) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <h2 className="text-lg font-semibold">
            {t("notes.list.empty_search_title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("notes.list.empty_search_body")}
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <FileText className="h-8 w-8 text-primary" />
        </div>
        <div className="max-w-sm space-y-1">
          <h2 className="text-lg font-semibold">
            {t("notes.list.empty_title.credit")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("notes.list.empty_body.credit")}
          </p>
        </div>
        <Button asChild>
          <Link
            to="/companies/$companyId/notas-credito/new"
            params={{ companyId }}
          >
            <Plus className="h-4 w-4" />
            {t("notes.list.new.credit")}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
